import  { Fragment, useEffect, useState } from "react";
import {
    useTable,
    useGlobalFilter,
    useSortBy,
    useFilters,
    useExpanded,
    usePagination,
    useRowSelect
} from "react-table";
import { withTranslation } from 'react-i18next';
import { Table, Row, DropdownItem, DropdownMenu, DropdownToggle, ButtonGroup, UncontrolledDropdown } from "reactstrap";
import Paginator from "Components/Common/Paginator";
import DtsTablePlaceholder from "Components/Common/FormBuilder/DtsTablePlaceholder";
import { getAvailableStatuses } from "helpers/workflowStatusHelper";
import { DropdownActions } from "Components/Common/Actions/DropdownActions";
import { BaseNavLink } from "../Base";
import { withPermission } from "Components/HOCs/withPermission";

const ActionBtn =  withPermission(BaseNavLink, {
    module: (props) => props.model,
    parent_model: (props) => props.model,
    action: (props) => props.action,
    featureKey:  (props) => props.model,
    hidePermission: true
});

interface TableContainerProps {
    columns?: any;
    data?: any;
    tableClass?: any;
    theadClass?: any;
    isPageSizeChange?: any;
    trClass?: any;
    thClass?: any;
    customPageSize?: any;
    handleLeadClick: (e: any) => void,
    setisDeleteOpen: (e: any) => void,
    pageChanged: (e: any) => void,
    pagination: any;
    onChangePageSize: (e: any) => void;
    handleSort: (e: any) => void;
    sortBy: string;
    sortOrder: string;
    props: any;
    viewsloading: any;
    dataLoading: boolean;
    metaLoading: any;
    model: any;
    parent: any;
    updateRow: any;
    onChange: (value: any, key: any, data: any, id: any) => void;
    url: any;
    checkbox: any;
    updatedCurrency: any;
    setIsViewAction?: (e: any) => void,
    setIsEditAction?: (e: any) => void,
    setisBulkDeleteOpen: (e: any) => void,
    setisBulkUpdateOpen:(e: any) => void,
    bulkSelectChBox: any;
    isViewDrawer: boolean;
    isEditDrawer: boolean;
    extraActions: any;
    stateLoading: any;
    selectedIds: any;
    isBulkActions?: boolean;
    saveState?: () => void;
}

const TableContainer = ({
    columns,
    data,
    isPageSizeChange,
    tableClass,
    theadClass,
    trClass,
    thClass,
    customPageSize,
    handleLeadClick,
    setisDeleteOpen,
    pageChanged,
    pagination,
    onChangePageSize,
    handleSort,
    sortOrder,
    sortBy,
    props,
    viewsloading,
    dataLoading,
    metaLoading,
    onChange,
    updateRow,
    checkbox,
    url,
    model,
    setIsViewAction,
    setIsEditAction,
    isViewDrawer,
    isEditDrawer,
    extraActions,
    stateLoading,
    bulkSelectChBox,
    selectedIds,
    setisBulkDeleteOpen,
    setisBulkUpdateOpen,
    isBulkActions,
    saveState
}: TableContainerProps) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        setPageSize,
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: 0, pageSize: customPageSize
            },
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );
    useEffect(() => {
        if (customPageSize) {
            setPageSize(Number(customPageSize));
        }
    }, [customPageSize]);

    const [isProcessing, setIsProcessing] = useState(true); // 🔹 New State to track data assignment
   const onChangeInSelect = (event: any) => {
        setPageSize(Number(event.target.value));
        onChangePageSize(event.target.value);
    };

    useEffect(() => {
        if (data?.length > 0) {
            setIsProcessing(true); // 🔹 Data received but still processing
        }
    }, [data]);

    // Handle processing state when `page` updates
    useEffect(() => {
        if (!dataLoading && page?.length > 0) {
            setIsProcessing(false); // 🔹 Processing done, page has data
        }
        else if (!stateLoading && !dataLoading && !page?.length) {
            setIsProcessing(false);
        }
    }, [page, dataLoading]);
   
    return (
        <div>
            {metaLoading || viewsloading || dataLoading || isProcessing || stateLoading ?
                <DtsTablePlaceholder
                    columns={columns}
                    theadClass={theadClass}
                    tableClass="align-middle table-nowrap"
                    thClass="border-bottom-1 table-soft-primary"
                />
                :
                <div>
                    <Row className="align-items-center mb-1 g-3 text-center text-sm-start">
                        <div className="col-sm">
                            <div className="text-muted">
                                {props.t("common.pagination.showing")}
                                <span className="fw-semibold ms-1">
                                    {data.length > 0 ? `${(pagination.offset ? pagination.offset : 0) + 1}-${Math.min((pagination.offset ? pagination.offset : 0) + pagination.size, pagination.elements)} ${props.t("common.pagination.of")} ${pagination.elements} ${props.t("common.pagination.results")}` : `0-0 of 0 results`}
                                </span>
                            </div>
                        </div>
                        <div className="col-sm-auto">
                            <Paginator total={pagination && pagination.elements ? pagination.elements : 0}
                                currentPage={pagination?.page}
                                pageChanged={(e: any) => pageChanged(e)}
                                pageSize={pagination.size}
                                isPageSizeChange={isPageSizeChange}
                                onChangeInSelect={(e: any) => onChangeInSelect(e)}
                                pagination={pagination}
                                props={props}
                            />
                        </div>
                    </Row>
                    <Row>
                        <div style={{minHeight:30}}>
                            {selectedIds?.length ? <div className="hstack gap-3 fs-14"><span>{selectedIds?.length} {props.t(`common.table.selected`)}</span>                            
                                    <ActionBtn className='text-primary cursor-pointer fs-12 fw-bold' action={'bulk edit'} toottipId='editBulkBtn-tooltip' onClick={() => setisBulkUpdateOpen(true)} model={model} permission={null}>
                                        <i className="ri-pencil-fill fs-14"></i>{" "}
                                        {props.t(`common.table.edit`)}
                                    </ActionBtn>
                                    <ActionBtn  className='text-danger cursor-pointer fs-12 fw-bold' action={'bulk delete'} toottipId='deleteBulkBtn-tooltip' onClick={() => setisBulkDeleteOpen(true)} model={model} permission={null}>
                                        <i className="ri-delete-bin-fill fs-14"></i>{" "}
                                        {props.t(`common.table.delete`)}
                                    </ActionBtn>
                           </div> : null}
                        </div>
                    </Row>
                    {!metaLoading && !dataLoading && !viewsloading && page && page.length ?
                        <div>
                            <Table responsive={true} hover {...getTableProps()} className={tableClass + 'm-0'}>
                                <thead className={theadClass}>
                                    {headerGroups.map((headerGroup: any) => (
                                        <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                                            {isBulkActions && bulkSelectChBox("all") && <th style={{minWidth: 100, verticalAlign: 'top'}}
                                            className={thClass + " fw-bold"}
                                            >{bulkSelectChBox("all")}</th>}  
                                            {checkbox && <th className={thClass + " fw-bold"} >Checkbox</th>}
                                            {headerGroup.headers.map((column: any) => (
                                                <th key={column.id} className={thClass + " fw-bold"}
                                                    {...column.isSortable ? { ...column.getSortByToggleProps() } : ""}>
                                                    <div onClick={() => handleSort(column.id)} className={"cursor-pointer" + `${column.accessor == 'course_name' ? '' : ' otherth'}`} title={column.Header}>
                                                        {column.render("Header")}
                                                        {sortBy === column.id && (
                                                            <span>
                                                                {sortOrder === 'asc' ? ' ▲' : ' ▼'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            ))}
                                            <th className={thClass + " fw-bold"} >
                                                Action
                                            </th>
                                        </tr>
                                    ))}
                                </thead>
                                <tbody {...getTableBodyProps()} style={{ minHeight: 300 }}>
                                    {page.map((row: any) => {
                                        prepareRow(row);
                                        return (
                                            <Fragment key={row.getRowProps().key}>
                                                <tr style={{ verticalAlign: 'middle', maxHeight: 50 }}>
                                                    {checkbox && <td>{checkbox(row)}</td>}
                                                    {isBulkActions && bulkSelectChBox(row) && <td style={{minWidth: 100, verticalAlign: 'middle', }}
                                            className={"align-items-center justify-content-between"}
                                            ><span className="hstack align-self-center">{bulkSelectChBox(row)}</span></td>}
                                                    {row.cells.map((cell: any) => {
                                                        let fields = cell.column.valueJson;
                                                        if (cell.column.id === 'student_status' || cell.column.id === 'application_status') {
                                                            let status_key = model == "students" ? "student_status" : "application_status"
                                                            const currentValue = row.original.id === updateRow?.id ? updateRow?.[status_key] : row.original[status_key];
                                                            const { statuses, status } = getAvailableStatuses(fields?.process_flows, currentValue, row?.original?.process_flow);
                                                            return <td>
                                                                <ButtonGroup>
                                                                    <UncontrolledDropdown>
                                                                        <DropdownToggle
                                                                            tag="button"
                                                                            // disabled={!(edit && (edit?.toLowerCase() === "all" || (edit?.toLowerCase() === "they_own" && they_own)))}
                                                                            className="btn btn-soft-dark text-left d-flex align-items-center justify-content-between bg-body-secondary"
                                                                            style={{ width: '160px', color: 'inherit' }}
                                                                        >
                                                                            {status?.label} <i className="mdi mdi-chevron-down"></i>
                                                                        </DropdownToggle>
                                                                        <DropdownMenu className="dropdownmenu-primary" style={{zIndex: 9999}}>
                                                                            {statuses.map((status: any, index: number) => (
                                                                                <DropdownItem
                                                                                    key={index}
                                                                                    onClick={(e) => onChange(status, fields.key, row.original, row.original.id)}
                                                                                >
                                                                                    {status?.label}
                                                                                </DropdownItem>
                                                                            ))}
                                                                        </DropdownMenu>
                                                                    </UncontrolledDropdown>
                                                                </ButtonGroup>

                                                            </td>
                                                        }
                                                        return (
                                                            <td
                                                                key={cell.id}
                                                                style={{ verticalAlign: 'middle' }}
                                                                className="align-items-center justify-content-between"
                                                                {...cell.getCellProps()}
                                                            >
                                                                {cell.render("Cell")}
                                                            </td>
                                                        );
                                                    })
                                                    }
                                                    <td>
                                                        <DropdownActions
                                                            row={row}
                                                            url={url}
                                                            permissions={row.original?.permissions}
                                                            extraActions={extraActions}
                                                            handleLeadClick={handleLeadClick}
                                                            setIsViewAction={setIsViewAction}
                                                            setIsEditAction={setIsEditAction}
                                                            setIsDeleteOpen={setisDeleteOpen}                                                            
                                                            isViewDrawer={isViewDrawer}
                                                            isEditDrawer={isEditDrawer}
                                                            translater={props.t}                                                            
                                                            module={model}
                                                            saveSessionState={saveState}
                                                        />
                                                    </td>
                                                </tr>
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table></div> :
                        <Table responsive={true} hover {...getTableProps()} className={tableClass + 'm-0'}>
                            <thead className={theadClass}>
                                {headerGroups && headerGroups.length ? headerGroups.map((headerGroup: any) => (
                                    <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column: any) => (
                                            <th key={column.id} className={thClass + " fw-bold"}
                                                {...column.isSortable ? { ...column.getSortByToggleProps() } : ""}

                                            >
                                                <div onClick={() => handleSort(column.id)} className={"cursor-pointer" + `${column.accessor == 'course_name' ? '' : ' otherth'}`} title={column.Header}>
                                                    {column.render("Header")}
                                                    {sortBy === column.id && (
                                                        <span>
                                                            {sortOrder === 'asc' ? ' ▲' : ' ▼'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                        <th className={thClass + " fw-bold"} >
                                            Action
                                        </th>
                                    </tr>
                                )) : null}
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan={headerGroups && headerGroups.length && headerGroups[0].headers.length + 1} className="p-5 fw-semibold fs-16 text-center">
                                        <div className="hstack p-5 justify-content-center fw-semibold fs-16 text-center w-100">
                                            {props.t("common.pagination.data_error")}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>}
                    <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                        <div className="col-sm">
                            <div className="text-muted">
                                {props.t("common.pagination.showing")}
                                <span className="fw-semibold ms-1">
                                    {data.length > 0 ? `${(pagination.offset ? pagination.offset : 0) + 1}-${Math.min((pagination.offset ? pagination.offset : 0) + pagination.size, pagination.elements)} ${props.t("common.pagination.of")} ${pagination.elements} ${props.t("common.pagination.results")}` : `0-0 of 0 ${props.t("common.pagination.results")}`}
                                </span>
                            </div>
                        </div>
                        <div className="col-sm-auto">
                            <Paginator total={pagination && pagination.elements ? pagination.elements : 0}
                                currentPage={pagination?.page}
                                pageChanged={(e: any) => pageChanged(e)}
                                pageSize={pagination.size}
                                isPageSizeChange={isPageSizeChange}
                                onChangeInSelect={(e: any) => onChangeInSelect(e)}
                                pagination={pagination}
                                props={props}
                            />
                        </div>
                    </Row>
                </div>}
        </div>
    );
};

export default withTranslation()(TableContainer);